import { type Rial, bpOf } from "@mfa-aff/shared";
import { groupThousands } from "./digits";

/**
 * Money on the wire and in the ledger is rial (BigInt, JSON string). The UI shows toman.
 * WHMCS amounts are toman; conversion happens only at the API boundary (spec §4.3).
 *
 * Exact money math (`bpOf`, `computeCodeSplit`, Toman parsing) lives in `@mfa-aff/shared`.
 * The functions here only parse the API's rial strings and format toman for display
 * (ASCII digits + U+066C grouping, drawn Persian by the font).
 */

export type { Rial };
export { bpOf };

const RIAL_PER_TOMAN = 10n;

/** Parses a rial amount (bigint or integer string). Never uses Number: money must stay exact. */
export function parseRial(value: bigint | string): bigint {
    if (typeof value === "bigint") return value;
    const trimmed = value.trim();
    if (!/^-?\d+$/.test(trimmed)) {
        throw new Error(`Invalid rial amount: ${value}`);
    }
    return BigInt(trimmed);
}

/** Rial → whole toman, truncated toward zero (a rial remainder is never shown as fake toman). */
export function rialToTomanWhole(value: bigint | string): bigint {
    return parseRial(value) / RIAL_PER_TOMAN;
}

/** Toman → rial. */
export function tomanToRial(toman: bigint | number): bigint {
    return BigInt(toman) * RIAL_PER_TOMAN;
}

export type TomanParts = {
    /** −1, 0 or 1. */
    sign: -1 | 0 | 1;
    /** Absolute toman amount grouped with ٬ (ASCII digits; the font draws them Persian). */
    grouped: string;
    /** Absolute toman amount. */
    toman: bigint;
};

export function toTomanParts(value: bigint | string): TomanParts {
    const toman = rialToTomanWhole(value);
    const sign: -1 | 0 | 1 = toman > 0n ? 1 : toman < 0n ? -1 : 0;
    const abs = toman < 0n ? -toman : toman;
    return { sign, toman: abs, grouped: groupThousands(abs.toString()) };
}

/**
 * "3٬400٬000 تومان". Negative amounts get a true minus sign (U+2212).
 * Pass `unit: false` for compact table cells where the column header carries the unit.
 */
export function formatToman(value: bigint | string, options: { unit?: boolean; signed?: boolean } = {}): string {
    const { unit = true, signed = false } = options;
    const { sign, grouped } = toTomanParts(value);
    const prefix = sign < 0 ? "−" : signed && sign > 0 ? "+" : "";
    return `${prefix}${grouped}${unit ? " تومان" : ""}`;
}

/**
 * Round amounts in words, for promises and headlines: 2,000,000 Rial → «200 هزار تومان»,
 * 50,000,000 Rial → «5 میلیون تومان». Anything not a whole thousand/million falls back to formatToman.
 */
export function formatTomanRound(value: bigint | string): string {
    const toman = parseRial(value) / RIAL_PER_TOMAN;
    if (toman > 0n && toman % 1_000_000n === 0n) return `${groupThousands((toman / 1_000_000n).toString())} میلیون تومان`;
    if (toman > 0n && toman % 1_000n === 0n && toman < 1_000_000n) return `${groupThousands((toman / 1_000n).toString())} هزار تومان`;
    return formatToman(value);
}

/** Compact toman for chart ticks: 12٫5 م (million), 3٫2 میلیارد. */
export function formatTomanCompact(toman: number): string {
    const abs = Math.abs(toman);
    const sign = toman < 0 ? "−" : "";
    const fmt = (n: number) => {
        const r = Math.round(n * 10) / 10;
        return String(r).replace(".", "٫");
    };
    if (abs >= 1_000_000_000) return `${sign}${fmt(abs / 1_000_000_000)} میلیارد`;
    if (abs >= 1_000_000) return `${sign}${fmt(abs / 1_000_000)} میلیون`;
    if (abs >= 1_000) return `${sign}${fmt(abs / 1_000)} هزار`;
    return `${sign}${fmt(abs)}`;
}

/** Rial string → toman as a JS number, for charts only (never for money math). */
export function rialToChartToman(value: bigint | string): number {
    return Number(rialToTomanWhole(value));
}
